import { Box, Flex, Heading, Skeleton } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { formatDate, useHasRole, useAppToast } from '../../../../helpers'
import { BlogPostService } from '../../../../services/BlogPost/BlogPostService'
import { BlogPost, PopularBlogPost } from '../../../../services/BlogPost/types'
import { PopularPostsModal } from './PopularPostsModal'

const BoxPending = ({ height, onClick }: { height: string; onClick?: () => void }) => (
  <Box
    bgImage={`linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))`}
    bgSize="cover"
    bgPos="center"
    bgRepeat="no-repeat"
    borderRadius="3xl"
    border="1px solid"
    borderColor="gray.300"
    height={height}
    width="420px"
    p={8}
    display="flex"
    flexDirection="column"
    justifyContent="space-between"
    gap={2}
    onClick={onClick}
    cursor="pointer"
  >
    <Box bgColor={'white'} borderRadius="full" px={'6'} py={'2'} alignSelf="flex-start">
      --/--/----
    </Box>
    <Heading color={'white'}>Post não selecionado</Heading>
  </Box>
)

const BoxPost = ({ post, height, onClick }: { post?: BlogPost; height: string; onClick?: () => void }) => {
  if (!post) {
    return <BoxPending height={height} onClick={onClick} />
  }

  return (
    <Box
      bgImage={`linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0)), url('${post.image}')`}
      bgSize="cover"
      bgPos="center"
      bgRepeat="no-repeat"
      borderRadius="3xl"
      border="1px solid"
      borderColor="gray.300"
      height={height}
      width="420px"
      p={8}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      gap={2}
      cursor="pointer"
      onClick={onClick}
    >
      <Box bgColor={'white'} borderRadius="full" px={'6'} py={'2'} alignSelf="flex-start">
        {formatDate(post.created_at)}
      </Box>
      <Heading color={'white'}>{post.title}</Heading>
    </Box>
  )
}

export default function PopularPostsPage() {
  const [popularPosts, setPopularPosts] = useState<PopularBlogPost[]>([])
  const toast = useAppToast()
  const service = new BlogPostService()
  // A posição fica guardada ao fechar: zerá-la junto trocava o título do modal para "0ª Posição"
  // durante a animação de saída.
  const [popularIndexEditing, setPopularIndexEditing] = useState<number>(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const hasRole = useHasRole()
  const permissions = {
    editPost: hasRole('edit_post')
  }

  // Cada card sai pela posição que o post ocupa, não pelo índice na lista: com uma posição vazia
  // no meio, os posts seguintes escorregavam um slot para trás.
  const postsByPosition: Record<number, PopularBlogPost> = Object.fromEntries(
    popularPosts.map((post) => [post.position, post])
  )

  const loadPopularPosts = async () => {
    try {
      const data = await service.listPopulars()
      setPopularPosts(data)
    } catch {
      toast.error('Erro ao carregar posts populares.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPopularPosts()
  }, [])

  const openModal = (position: number) => {
    setPopularIndexEditing(position)
    setIsModalOpen(true)
  }

  const renderSlot = (position: number, height: string) => (
    <BoxPost
      post={postsByPosition[position]}
      height={height}
      onClick={permissions.editPost ? () => openModal(position) : undefined}
    />
  )

  return (
    <>
      <Heading as="h2" marginY="5" mb="10">
        Posts mais populares
      </Heading>

      {isLoading ? (
        <Flex direction="row" justify="center" gap={7}>
          <Flex direction="column" gap={7}>
            <Skeleton height="250px" width="420px" borderRadius="3xl" p={8} />
            <Skeleton height="250px" width="420px" borderRadius="3xl" p={8} />
          </Flex>
          <Skeleton height="530px" width="420px" borderRadius="3xl" p={8} />
          <Flex direction="column" gap={7}>
            <Skeleton height="250px" width="420px" borderRadius="3xl" p={8} />
            <Skeleton height="250px" width="420px" borderRadius="3xl" p={8} />
          </Flex>
        </Flex>
      ) : (
        <Flex direction="row" justify="center" gap={7}>
          <Flex direction="column" gap={7}>
            {renderSlot(1, '250px')}
            {renderSlot(2, '250px')}
          </Flex>
          {renderSlot(3, '530px')}
          <Flex direction="column" gap={7}>
            {renderSlot(4, '250px')}
            {renderSlot(5, '250px')}
          </Flex>
        </Flex>
      )}

      <PopularPostsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={loadPopularPosts}
        popularIndex={popularIndexEditing}
      />
    </>
  )
}
